import { SelectorEngine } from '@runtime/SelectorEngine';
import { MAX_HIGHLIGHTED_ELEMENTS } from '@shared/constants';

export class Highlighter {
  private selectorEngine: SelectorEngine;
  private highlights: HTMLElement[] = [];
  private badge: HTMLElement | null = null;
  private currentSelector: string = '';

  constructor(selectorEngine: SelectorEngine) {
    this.selectorEngine = selectorEngine;
    this.createBadge();
    this.setupEventListeners();
  }

  private createBadge(): void {
    this.badge = document.createElement('div');
    this.badge.className = 'ce-target-badge';
    this.badge.style.cssText = `
      position: fixed;
      z-index: 999999998;
      pointer-events: none;
      font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial;
      font-weight: 900;
      font-size: 12px;
      color: #00e5ff;
      background: rgba(17,17,17,.92);
      border: 1px solid rgba(0,229,255,.35);
      border-radius: 999px;
      padding: 3px 8px;
      display: none;
    `;
    document.documentElement.appendChild(this.badge);
  }

  private setupEventListeners(): void {
    const updatePositions = () => {
      if (this.currentSelector) {
        this.updateHighlights();
      }
    };

    window.addEventListener('scroll', updatePositions, true);
    window.addEventListener('resize', updatePositions);
  }

  show(selector: string): void {
    this.clear();
    this.currentSelector = selector;

    const elements = this.selectorEngine.getHighlightedElements(selector);
    if (elements.length === 0) return;

    const shown = elements.slice(0, MAX_HIGHLIGHTED_ELEMENTS);

    // Create highlight boxes
    shown.forEach((element) => {
      if (!(element instanceof Element)) return;

      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const box = document.createElement('div');
      box.className = 'ce-target-box';
      box.style.cssText = `
        position: fixed;
        z-index: 999999998;
        pointer-events: none;
        border: 2px solid #00e5ff;
        border-radius: 6px;
        box-shadow: 0 0 0 9999px rgba(0,0,0,.06);
        top: ${Math.max(0, rect.top)}px;
        left: ${Math.max(0, rect.left)}px;
        width: ${Math.max(0, rect.width)}px;
        height: ${Math.max(0, rect.height)}px;
      `;

      document.documentElement.appendChild(box);
      this.highlights.push(box);
    });

    // Update badge
    if (this.badge) {
      const message =
        elements.length > MAX_HIGHLIGHTED_ELEMENTS
          ? `Matches: ${elements.length} (showing ${MAX_HIGHLIGHTED_ELEMENTS})`
          : `Matches: ${elements.length}`;

      this.badge.textContent = message;
      this.badge.style.display = 'block';

      // Position badge near first element
      if (shown.length > 0) {
        const firstRect = shown[0].getBoundingClientRect();
        this.badge.style.top = Math.max(8, firstRect.top - 28) + 'px';
        this.badge.style.left = Math.max(8, firstRect.left) + 'px';
      }
    }
  }

  clear(): void {
    this.currentSelector = '';

    // Remove highlight boxes
    this.highlights.forEach((box) => {
      try {
        box.remove();
      } catch (error) {
        // Ignore errors during removal
      }
    });
    this.highlights = [];

    // Hide badge
    if (this.badge) {
      this.badge.style.display = 'none';
    }
  }

  private updateHighlights(): void {
    if (!this.currentSelector) return;

    const elements = this.selectorEngine.getHighlightedElements(this.currentSelector);
    const shown = elements.slice(0, MAX_HIGHLIGHTED_ELEMENTS);

    // Update highlight box positions
    for (let i = 0; i < this.highlights.length && i < shown.length; i++) {
      const box = this.highlights[i];
      const element = shown[i];

      if (!box || !element) continue;

      const rect = element.getBoundingClientRect();
      box.style.top = Math.max(0, rect.top) + 'px';
      box.style.left = Math.max(0, rect.left) + 'px';
      box.style.width = Math.max(0, rect.width) + 'px';
      box.style.height = Math.max(0, rect.height) + 'px';
    }

    // Update badge position
    if (this.badge && shown.length > 0) {
      const firstRect = shown[0].getBoundingClientRect();
      this.badge.style.top = Math.max(8, firstRect.top - 28) + 'px';
      this.badge.style.left = Math.max(8, firstRect.left) + 'px';
    }
  }

  destroy(): void {
    this.clear();
    if (this.badge) {
      this.badge.remove();
      this.badge = null;
    }
  }
}
